import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Link, NavLink } from 'react-router-dom';
import {
  Book,
  Upload,
  Search,
  Settings as SettingsIcon,
  Cpu,
  Home,
  Network,
  MoreHorizontal,
  X,
} from 'lucide-react';
import { SIDEBAR_PILLARS } from './config/sidebarNav';
import Notifications from './components/Notifications';
import TaskCenter from './components/TaskCenter';
import ToastProvider from './components/ToastProvider';
import { QueryClientProvider } from '@tanstack/react-query';
import { AppProvider, useAppContext } from './context/AppContext';
import { queryClient } from './queryClient';
import PerformanceMonitor from './components/PerformanceMonitor';
import ErrorBoundary from './utils/errorBoundary';
import { initApiConfig, checkApiConnectivity, API_CONFIG } from './config/api';
import PWAStatus from './components/PWAStatus';
import OnboardingWizard from './components/OnboardingWizard';
import OnboardingTrigger from './components/OnboardingTrigger';
import { ConfirmProvider } from './components/ConfirmDialog';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './components/LoginPage';
import './components/AuthGate.css';
import { LogOut } from 'lucide-react';
import { AppRoutes } from './routes';
import './App.css';
import './components/CrossBrowserCompatibility.css';
import { useOrigin, OriginProvider } from './origin/OriginContext';
import OriginFilament from './origin/OriginFilament';
import OriginGlass from './origin/OriginGlass';
import OriginModule from './origin/OriginModule';
import OriginSignatureLine from './origin/OriginSignatureLine';
import OriginLoader from './origin/OriginLoader';
const APP_VERSION = '2.3.0';

const FIRST_RUN_FLAG_KEY = 'biblio_first_run_seen';

function AppShell() {
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const { setState: setOriginState } = useOrigin();
  const { setOnboardingTourRun } = useAppContext();
  const { user, authRequired, dataLocality, logout } = useAuth();
  const [apiConn, setApiConn] = useState({ state: 'checking', message: 'Vérification...' });

  // Lance automatiquement le tutoriel à la toute première visite (AUD-008).
  useEffect(() => {
    try {
      const seen = localStorage.getItem(FIRST_RUN_FLAG_KEY);
      if (!seen) {
        localStorage.setItem(FIRST_RUN_FLAG_KEY, '1');
        // Léger délai pour laisser le DOM (sidebar, bottom-nav) se rendre avant que Joyride
        // ne calcule les positions des ancres `.tour-*`.
        const t = setTimeout(() => setOnboardingTourRun(true), 800);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // localStorage indisponible (mode privé strict) : on ne lance pas le tour pour éviter une boucle.
      console.warn('[AppShell] localStorage indisponible — first-run skip', e);
    }
    return undefined;
  }, [setOnboardingTourRun]);

  // Gestion de l'invalidation du cache (Hard Reload si version différente)
  useEffect(() => {
    const savedVersion = localStorage.getItem('app_version');
    if (savedVersion && savedVersion !== APP_VERSION) {
      console.log(`Nouvelle version détectée : ${APP_VERSION} (actuelle : ${savedVersion}). Rechargement forcé...`);
      localStorage.setItem('app_version', APP_VERSION);
      window.location.reload(true);
    } else {
      localStorage.setItem('app_version', APP_VERSION);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timeoutId = null;

    // Backoff dynamique : on poll vite tant que l'API est KO (10s) puis on espace à 60s
    // une fois la connexion établie. Évite les "silent failures" pendant 60s du précédent code.
    const HEALTHY_INTERVAL = 60000;
    const UNHEALTHY_INTERVAL = 10000;

    const refresh = async () => {
      const r = await checkApiConnectivity();
      if (cancelled) return;

      let nextDelay = HEALTHY_INTERVAL;

      if (r.status === 'connected') {
        setOriginState('success');
        setApiConn({
          state: 'ok',
          message: `API connectée (${r.responseTime} ms)`,
        });
      } else if (r.status === 'unreachable') {
        setOriginState('error');
        setApiConn({
          state: 'error',
          message: 'API inaccessible — vérifiez que le backend est démarré',
        });
        nextDelay = UNHEALTHY_INTERVAL;
      } else {
        setOriginState('error');
        setApiConn({
          state: 'error',
          message:
            r.statusCode != null
              ? `API : ${r.statusCode} ${r.statusText || ''}`.trim()
              : 'Erreur API',
        });
        nextDelay = UNHEALTHY_INTERVAL;
      }

      timeoutId = setTimeout(refresh, nextDelay);
    };

    refresh();

    // Re-check immédiat quand la fenêtre revient au premier plan ou quand
    // l'OS signale un retour de réseau (ne pas attendre l'intervalle).
    const onFocus = () => {
      if (timeoutId) clearTimeout(timeoutId);
      refresh();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', onFocus);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', onFocus);
    };
  }, [setOriginState]);

  return (
    <>
      <OriginLoader />
      <OriginFilament />
      <div className="app" data-testid="app-container">
              <OriginGlass variant="soft" className="sidebar" noAnimation>
                <div className="nav-header">
                  <OriginModule />
                  <div className="nav-header-text">
                    <h1>Biblio-Tech RAG</h1>
                    <p className="nav-subtitle">
                      {dataLocality === 'cloud' ? 'Supabase • IA privée' : 'Local-first • IA privée'}
                    </p>
                  </div>
                </div>
                <OriginSignatureLine />

                <div className="nav-links">
                  {SIDEBAR_PILLARS.map((pillar) => {
                    // main n'expose pas de role cote client : on n'introduit pas ici de
                    // filtrage par role, ce serait un changement de comportement hors
                    // du perimetre visuel. adminOnly reste une metadonnee de config.
                    const visibles = pillar.items;
                    if (visibles.length === 0) return null;
                    return (
                      <div key={pillar.id} className="nav-more-group nav-pillar">
                        <div className="nav-more-group-label">{pillar.label}</div>
                        {visibles.map((item) => {
                          const {
                            Icon,
                            key: navKey,
                            className: tourClass,
                            title,
                            label,
                            adminOnly: _adminOnly,
                            ...linkProps
                          } = item;
                          return (
                            <NavLink
                              key={navKey}
                              {...linkProps}
                              title={title}
                              className={({ isActive }) =>
                                [isActive ? 'nav-link active' : 'nav-link', tourClass || ''].filter(Boolean).join(' ')
                              }
                            >
                              <Icon size={20} aria-hidden />
                              <span>{label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>

                <div className="nav-footer">
                  <div
                    className="status-indicator"
                    title={API_CONFIG.API_BASE_URL}
                  >
                    <div
                      className={`status-dot${apiConn.state === 'ok' ? ' online' : apiConn.state === 'checking' ? ' checking' : ' offline'}`}
                      aria-hidden
                    />
                    <span className="status-indicator-text">{apiConn.message}</span>
                  </div>

                  {/* Centre de notifications */}
                  <div className="notifications-container">
                    <Notifications />
                  </div>

                  <div className="onboarding-trigger-desktop">
                    <OnboardingTrigger />
                  </div>

                  {/* Bouton Performance Monitor */}
                  <button
                    onClick={() => setShowPerformanceMonitor(true)}
                    className="performance-monitor-btn btn-origin"
                    title="Monitoring Performance"
                    aria-label="Ouvrir le moniteur de performance"
                  >
                    <Cpu size={16} />
                    <span>Performance</span>
                  </button>

                  {/* Déconnexion : visible uniquement quand l'auth est active */}
                  {authRequired && (
                    <button
                      onClick={logout}
                      className="performance-monitor-btn btn-origin"
                      title={user ? `Connecté : ${user.username}` : 'Se déconnecter'}
                      aria-label="Se déconnecter"
                    >
                      <LogOut size={16} />
                      <span>{user ? user.username : 'Déconnexion'}</span>
                    </button>
                  )}
                </div>
              </OriginGlass>

              <main className="main-content" role="main">
                <AppRoutes />
              </main>

              <nav className="bottom-nav" aria-label="Navigation mobile">
                <NavLink
                  to="/upload"
                  className={({ isActive }) =>
                    isActive
                      ? 'bottom-nav-link is-active tour-bottom-upload tour-upload-btn'
                      : 'bottom-nav-link tour-bottom-upload tour-upload-btn'
                  }
                >
                  <Upload size={24} strokeWidth={2} />
                  <span>Import</span>
                </NavLink>
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) => (isActive ? 'bottom-nav-link is-active' : 'bottom-nav-link')}
                >
                  <Home size={24} strokeWidth={2} />
                  <span>Accueil</span>
                </NavLink>
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) => (isActive ? 'bottom-nav-link is-active' : 'bottom-nav-link')}
                >
                  <Book size={24} strokeWidth={2} />
                  <span>Biblio</span>
                </NavLink>
                <NavLink
                  to="/ia"
                  className={({ isActive }) => (isActive ? 'bottom-nav-link is-active' : 'bottom-nav-link')}
                >
                  <Cpu size={24} strokeWidth={2} />
                  <span>IA</span>
                </NavLink>
                <NavLink
                  to="/recherche"
                  className={({ isActive }) => (isActive ? 'bottom-nav-link is-active' : 'bottom-nav-link')}
                >
                  <Search size={24} strokeWidth={2} />
                  <span>Recherche</span>
                </NavLink>
                {/* Entrée « Carte » retirée avec la route /canvas : l'Espace
                    spatial ne persistait que dans le localStorage et créait des
                    citations factices. */}
                <NavLink
                  to="/graphes"
                  className={({ isActive }) => (isActive ? 'bottom-nav-link is-active' : 'bottom-nav-link')}
                >
                  <Network size={24} strokeWidth={2} />
                  <span>Graphe</span>
                </NavLink>
                <NavLink
                  to="/parametres"
                  className={({ isActive }) => (isActive ? 'bottom-nav-link is-active' : 'bottom-nav-link')}
                >
                  <SettingsIcon size={24} strokeWidth={2} />
                  <span>Réglages</span>
                </NavLink>
                <button
                  type="button"
                  className={`bottom-nav-link bottom-nav-more${mobileMoreOpen ? ' is-active' : ''}`}
                  aria-expanded={mobileMoreOpen}
                  aria-controls="mobile-more-sheet"
                  onClick={() => setMobileMoreOpen((o) => !o)}
                >
                  <MoreHorizontal size={24} strokeWidth={2} />
                  <span>Plus</span>
                </button>
              </nav>

              <div className="onboarding-trigger-mobile-only">
                <OnboardingTrigger />
              </div>

              {mobileMoreOpen && (
                <>
                  <button
                    type="button"
                    className="mobile-more-backdrop"
                    aria-label="Fermer le menu"
                    onClick={() => setMobileMoreOpen(false)}
                  />
                  <div
                    id="mobile-more-sheet"
                    className="mobile-more-sheet"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="mobile-more-title"
                  >
                    <div className="mobile-more-sheet-header">
                      <h2 id="mobile-more-title" className="mobile-more-sheet-title">
                        Autres pages
                      </h2>
                      <button
                        type="button"
                        className="mobile-more-sheet-close"
                        onClick={() => setMobileMoreOpen(false)}
                        aria-label="Fermer"
                      >
                        <X size={24} />
                      </button>
                    </div>
                    {/* Mobile : itère sur SIDEBAR_PILLARS pour rester aligné avec la nav desktop (AUD-008). */}
                    <div className="mobile-more-sheet-links">
                      {SIDEBAR_PILLARS.map((section) => (
                        <div key={section.id} className="mobile-more-section">
                          <div className="mobile-more-section-label">{section.label}</div>
                          {section.items.map((item) => {
                            const { Icon, key: itemKey, label, title, to } = item;
                            return (
                              <Link
                                key={itemKey}
                                to={to}
                                title={title}
                                className="mobile-more-link"
                                onClick={() => setMobileMoreOpen(false)}
                              >
                                <Icon size={20} aria-hidden /> {label}
                              </Link>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Indicateur de statut PWA */}
              <PWAStatus />

              {/* Tutoriel interactif (react-joyride) — lancement manuel uniquement */}
              <OnboardingWizard />
            </div>

      {/* Centre de tâches global */}
      <TaskCenter />

      <PerformanceMonitor
        isOpen={showPerformanceMonitor}
        onClose={() => setShowPerformanceMonitor(false)}
      />
    </>
  );
}

/**
 * Gate conditionnel : décide quoi afficher selon l'état d'authentification.
 * - 'loading'   : splash de marque le temps du bootstrap ;
 * - 'anonymous' : écran de connexion (uniquement si l'auth est requise) ;
 * - sinon       : l'application.
 * Chaque état porte la marque « Biblio-Tech RAG » (splash, login, shell).
 */
function AuthGate({ children }) {
  const { status, authRequired } = useAuth();

  if (status === 'loading') {
    return (
      <div className="auth-gate-splash" role="status" aria-live="polite">
        <OriginLoader />
        <h1 className="auth-gate-splash-title">Biblio-Tech RAG</h1>
        <p className="auth-gate-splash-text">Initialisation…</p>
      </div>
    );
  }

  if (authRequired && status === 'anonymous') {
    return <LoginPage />;
  }

  return children;
}

function App({ queryClient: queryClientProp } = {}) {
  useEffect(() => {
    initApiConfig();
  }, []);

  const qc = queryClientProp ?? queryClient;

  return (
    <QueryClientProvider client={qc}>
      <ErrorBoundary>
        <AppProvider>
          <ToastProvider>
            <ConfirmProvider>
              <OriginProvider>
                <AuthProvider>
                  <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <AuthGate>
                      <AppShell />
                    </AuthGate>
                  </Router>
                </AuthProvider>
              </OriginProvider>
            </ConfirmProvider>
          </ToastProvider>
        </AppProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;

